import { z } from 'zod'
import type { ProductCategoryId } from '../constants'
import type { MenuTags } from '../menu-tags'
import type { ProductTags } from '../product-tags'
import { AXES, type Axis } from '../taxonomy'
import { LANGUAGES, type Currency, type DisplayCurrency, type Language } from './simulation'

/**
 * 완성 사례 갤러리 (2단계, 지시서 P1-01, 2026-10-22).
 * 설계: release_kit/52_샘플_콘텐츠/20_2단계_완성사례_갤러리_설계.md 5~7장.
 * 사례 내용은 엑셀에서 가져오기 도구로만 들어온다. 앱과 관리자는 읽기와 관리자 칸(숨김·시작 허용·순위)만 바꾼다.
 */

/** 기능 이름. GET /config 의 features.sampleCases(서버 코드) 와 settings.sampleCases(관리자 설정) 에 같은 이름을 쓴다 */
export const SAMPLE_CASES_FEATURE = 'sampleCases' as const

/**
 * POST /simulations 409 — sourceCaseId 가 있는데 사례 시작이 꺼져 있다(settings.sampleCases.enabled·startEnabled).
 * 비상 스위치를 끈 뒤에도 이미 받은 시작 입력·60초 캐시로 생성되지 않게, 사용권을 쓰기 전에 거절한다 (1.0.6 점검 P2-21)
 */
export const CASE_START_DISABLED_CODE = 'CASE_START_DISABLED'

/**
 * 사례 종류 (2026-11-01, 대표 요청 '국가별 요리 템플릿').
 * - case: 실제로 만든 제품의 완성 사례. 판매가·1개 원가·마진이 있다 (1.0.5 부터의 사례).
 * - template: 국가별 대표 요리를 운영팀이 정리한 시작용 틀. 숫자·사진이 없고, 시작하면 만들기 초안만 채운다(생성은 사용자가 [만들기]를 누를 때).
 * 옛 앱(1.0.9 까지)은 template 을 모른다. 서버는 kind 질의가 없으면 case 만 돌려준다 (caseListQuerySchema)
 */
export const SAMPLE_CASE_KINDS = ['case', 'template'] as const
export type SampleCaseKind = (typeof SAMPLE_CASE_KINDS)[number]
/** 목록·상세 질의의 kind. all = 두 종류 모두 (새 앱) */
export const CASE_QUERY_KINDS = ['case', 'template', 'all'] as const
export type CaseQueryKind = (typeof CASE_QUERY_KINDS)[number]

/**
 * 셰프 크레딧 상태 (2026-11-01, 2026-11-02 menu 추가).
 * - none: 크레딧 없음 (기본). 원본 엑셀의 '셰프' 칸에서 크레딧을 짐작해 넣지 않는다
 * - verified: 셰프가 준 레시피 글(재료·만드는 법)과 서면 동의가 있다. 앱은 '<이름> 셰프 레시피' 라고 쓴다
 * - menu: 셰프가 이 요리가 자기 메뉴임을 서면으로 확인했다(대표 확인). 레시피 글은 셰프 것이 아니라
 *   분량·만드는 법을 앱이 새로 계산한다. 앱은 '<이름> 셰프 메뉴' 라고 쓴다
 * API 는 verified·menu 일 때만 크레딧을 보낸다. 이 기능 전 빌드(1.0.10 후보 빌드 24 포함)는 verified 만 그리고
 * 사진 저작권 줄도 그리지 못한다. 그래서 서버는 CLIENT_FEATURE_CASE_CREDITS 를 보내지 않는 앱에 menu 템플릿을 주지 않고,
 * 저작권 표시가 붙은 사진도 빼고 보낸다 (caseForClient·withoutCredits)
 */
export const CHEF_CREDIT_STATUSES = ['none', 'verified', 'menu'] as const
export type ChefCreditStatus = (typeof CHEF_CREDIT_STATUSES)[number]
/** 앱에 보내는 크레딧 단계 (none 은 보내지 않는다) */
export type ChefCreditLevel = Exclude<ChefCreditStatus, 'none'>

/**
 * X-Client-Features 값: 셰프 메뉴 크레딧(menu)·사진 저작권 줄(imageCredits·photoCredit)·원문 링크를 그릴 줄 안다 (2026-11-02).
 * 이 값이 없는 앱(1.0.9 까지와 1.0.10 후보 빌드 24)에는 서버가 menu 템플릿을 목록·상세에서 빼고, 저작권 표시가 필요한
 * 사진(사례 사진·셰프 사진)을 비워 보낸다 — 표시 없이 CC BY·CC BY-SA 사진이 보이지 않게 (라이선스 조건)
 */
export const CLIENT_FEATURE_CASE_CREDITS = 'case-credits'

/** 셰프 이름 4개 언어 (셰프가 승인한 표기) */
export type ChefName = Record<Language, string>

/**
 * 사진 저작권 표시 (2026-11-02). 자유 라이선스 사진(Wikimedia Commons 의 CC0·퍼블릭 도메인·CC BY·CC BY-SA)을 쓸 때
 * 사진 아래에 저작자·라이선스를 적고 원본 파일 페이지로 잇는다. 셰프가 직접 준 사진에는 붙지 않는다
 */
export interface CasePhotoCredit {
  /** 저작자 (글자 그대로) */
  author: string
  /** 라이선스 짧은 이름 (예: 'CC BY-SA 4.0', 'CC0', 'Public domain') */
  license: string
  /** 라이선스 본문 주소. 퍼블릭 도메인은 없을 수 있다 */
  licenseUrl: string | null
  /** 원본 파일 페이지 (예: https://commons.wikimedia.org/wiki/File:...) */
  sourceUrl: string
}

/** 셰프 원문 링크 종류. youtube = 셰프 공식 채널 영상, website = 셰프 공식 사이트, broadcaster = 방송사 공식 프로그램 페이지 */
export const CHEF_SOURCE_KINDS = ['youtube', 'website', 'broadcaster'] as const
export type ChefSourceKind = (typeof CHEF_SOURCE_KINDS)[number]

/** 셰프 원문 링크 (공식 채널·사이트·방송사 페이지만, 재업로드 금지). 앱은 '방송·원문 보기' 로 밖에서 연다 */
export interface ChefSourceLink {
  url: string
  kind: ChefSourceKind
}

/**
 * 앱에 보내는 셰프 크레딧. status 가 verified·menu 인 사례에만 붙는다 (없으면 칸이 없다).
 * 동의 문서 위치(chefConsentRef)와 레시피 출처 메모(chefRecipeSource)는 관리자 전용이라 여기 없다.
 * photoCredit·source 는 1.0.10 서버부터 있다 (옛 서버는 칸이 없다)
 */
export interface CaseChefCredit {
  status: ChefCreditLevel
  name: ChefName
  /** 셰프 사진의 R2 키 (GET /community/images/<key>). 없으면 null */
  photoKey: string | null
  /** 셰프 사진이 자유 라이선스 사진이면 그 표시. 셰프가 준 사진이거나 사진이 없으면 null */
  photoCredit?: CasePhotoCredit | null
  /** 셰프 원문 링크. 없으면 null */
  source?: ChefSourceLink | null
}

/** 질의 kind 가 이 종류의 사례를 받는지 */
export function caseKindAllowed(kind: SampleCaseKind, query: CaseQueryKind): boolean {
  return query === 'all' || query === kind
}

/** 샘플번호 정규화 모양 (KR-001). 앞 2자가 나라다 (데이터 계약 2.2) */
export const SAMPLE_NO_RE = /^[A-Z]{2}-\d{3,4}$/
/** 나라 코드 = 샘플번호 앞 2자 */
const CASE_COUNTRY_RE = /^[A-Z]{2}$/

/** 엑셀 한도와 같은 값 (데이터 계약 2.2~2.4). 시작 입력 길이 계산(composeCaseStartMessages)도 이 값을 전제로 한다 */
export const SAMPLE_CASE_LIMITS = {
  name: 40,
  tagline: 140,
  format: 60,
  ingredients: 25,
  ingredientName: 80,
  unit: 20,
  steps: 10,
  step: 200,
  images: 4,
} as const

/**
 * 사례 재료 한 줄 (sample_cases.ingredients). 양 비공개 사례는 qty·unit·unitPrice 가 null 이다.
 * unitPrice 는 저장 통화(KRW·USD) 기준이다
 */
export const sampleCaseIngredientSchema = z.object({
  order: z.number().int().min(1).max(SAMPLE_CASE_LIMITS.ingredients),
  name: z.string().min(1).max(SAMPLE_CASE_LIMITS.ingredientName),
  qty: z.number().positive().max(1_000_000).nullable(),
  unit: z.string().min(1).max(SAMPLE_CASE_LIMITS.unit).nullable(),
  unitPrice: z.number().nonnegative().nullable(),
})
export type SampleCaseIngredient = z.infer<typeof sampleCaseIngredientSchema>

/** GET /cases 질의 (손님 가능) */
export const caseListQuerySchema = z.object({
  /** 이 언어 사례만. 없으면 모든 언어 */
  lang: z.enum(LANGUAGES).optional(),
  /** 나라 칩 (샘플번호 앞 2자) */
  country: z.string().regex(CASE_COUNTRY_RE).optional(),
  /** 축 칩 (식품·메뉴·코스). 없으면 모든 축. 옛 서버는 모르는 질의라 버리고 모든 축을 준다 */
  axis: z.enum(AXES).optional(),
  /** 사례 종류. 없으면 case 만 — 옛 앱(1.0.9 까지)이 숫자 없는 템플릿을 받지 않게 한다 */
  kind: z.enum(CASE_QUERY_KINDS).default('case'),
  limit: z.coerce.number().int().min(1).max(50).default(30),
  offset: z.coerce.number().int().min(0).max(1000).default(0),
})
export type CaseListQuery = z.infer<typeof caseListQuerySchema>

/** GET /cases/:id 질의. 템플릿 상세는 kind 가 template·all 일 때만 연다 (옛 앱은 404 를 받는다) */
export const caseDetailQuerySchema = z.object({
  kind: z.enum(CASE_QUERY_KINDS).default('case'),
})
export type CaseDetailQuery = z.infer<typeof caseDetailQuerySchema>

/** 목록 카드 */
export interface CaseCard {
  id: string
  sampleNo: string
  country: string
  language: Language
  axis: Axis
  majorId: string
  subId: string | null
  categoryId: string
  /** 사례 종류. 옛 서버 응답에는 없다 (없으면 case) */
  kind?: SampleCaseKind
  name: string
  tagline: string
  format: string
  /** 엑셀에 적은 통화와 금액 그대로. 템플릿(kind template)이면 숫자가 없어 null 이다. 통화 칸은 언어 기본값 */
  sourceCurrency: DisplayCurrency
  sourceRetail: number | null
  sourceUnitCost: number | null
  /** (판매가 - 1개 원가) / 판매가 x 100 을 반올림. 글 제목과 같은 계산 (데이터 계약 3.3). 템플릿이면 null */
  marginPct: number | null
  /** 환산 표시용 저장 통화 값. 템플릿이면 null */
  currency: Currency
  retail: number | null
  /** 썸네일 키 (GET /community/images/<key>). 없으면 첫 사진 키. 사진 없는 템플릿이면 '' (앱이 분류 사진을 쓴다) */
  thumbKey: string
  imageCount: number
  /** 셰프 크레딧. verified·menu 인 사례에만 있다. 옛 서버·크레딧 없는 사례는 칸이 없다 */
  chefCredit?: CaseChefCredit
}

/** 템플릿 사례인지. 옛 서버 응답(kind 없음)은 완성 사례다 */
export function isTemplateCase(card: Pick<CaseCard, 'kind'>): boolean {
  return card.kind === 'template'
}

/** 나라 칩 하나 */
export interface CaseCountryCount {
  code: string
  count: number
}

/** GET /cases 응답 */
export interface CaseListResponse {
  items: CaseCard[]
  /** 거르기 조건(lang, country)에 맞는 전체 수 */
  total: number
  /** 다음 쪽 offset. 끝이면 null */
  nextOffset: number | null
  /** offset 0 일 때만. 같은 lang 조건으로 센 나라별 수 (많은 순). 다음 쪽에서는 null */
  countries: CaseCountryCount[] | null
}

/**
 * '이 사례로 시작하기' 입력. 앱은 이 값을 만들기 초안에 그대로 넣고 sourceCaseId 를 더한다 (설계 4.4, 6.3). 문구는 서버가 만든다.
 * - 완성 사례: mode analyze. 앱이 확인 창을 거쳐 바로 기존 POST /simulations 로 보낸다 (composeCaseStartMessages)
 * - 템플릿: mode create. 앱은 초안만 채워 설명 화면으로 간다. 생성은 사용자가 [만들기]를 누를 때다 (composeTemplateStartMessages)
 */
export interface CaseStartInput {
  mode: 'analyze' | 'create'
  axis: Axis
  majorId: string
  subId: string | null
  categoryId: ProductCategoryId
  messages: { text: string }[]
  /** 템플릿의 메뉴·코스 꼬리표 (정리한 값). 완성 사례에는 없다 */
  menuTags?: MenuTags
  /** 템플릿의 식품 꼬리표 (정리한 값). 완성 사례에는 없다 */
  productTags?: ProductTags
}

/** 상세 */
export interface CaseDetail extends CaseCard {
  /** 사례 사진 R2 키 (GET /community/images/<key>), 1~4장. 1.0.5 출시 때의 사례는 운영팀 예시 사례다 (10/24) */
  images: string[]
  /**
   * images 와 같은 순서의 사진 저작권 표시 (2026-11-02). 표시가 없는 사진은 null. 표시가 하나도 없거나 옛 서버면 칸이 없다
   */
  imageCredits?: (CasePhotoCredit | null)[]
  /** 재료 기준 개수 */
  batchUnits: number
  /** 양 공개. false 면 ingredients 의 qty·unit·unitPrice 가 모두 null */
  recipePublic: boolean
  ingredients: SampleCaseIngredient[]
  /** 만드는 법, 순서대로 */
  steps: string[]
  /** 1개당 재료비 (엑셀 통화). 모든 재료에 양과 단가가 있을 때만 */
  sourceIngredientCost: number | null
  /** 템플릿의 꼬리표 (메뉴·코스 축은 MenuTags, 식품 축은 ProductTags 모양). 완성 사례·옛 서버는 없다 */
  tags?: MenuTags | ProductTags
  /** 같은 번호의 1단계 글. 지워졌으면 null (댓글 버튼을 그리지 않는다). 템플릿은 글이 없어 늘 null */
  postId: string | null
  commentCount: number
  /** 시작할 수 없으면 null (사례 시작 꺼짐, 설정 startEnabled 꺼짐, 분류가 지금 분류표에 없음) */
  startInput: CaseStartInput | null
}

/** GET /cases/:id 응답 */
export interface CaseDetailResponse {
  case: CaseDetail
}

// ── 관리자 ────────────────────────────────────────────────────────────

/** 관리자 목록의 상태 거르기. visible = 보임, hidden = 관리자 숨김, deleted = 엑셀 '내리기' */
export const ADMIN_CASE_STATUSES = ['visible', 'hidden', 'deleted'] as const
export type AdminCaseStatus = (typeof ADMIN_CASE_STATUSES)[number]

/** GET /admin/cases 질의. kind 가 없으면 두 종류 모두 (관리자는 템플릿도 본다) */
export const adminCaseListQuerySchema = z.object({
  lang: z.enum(LANGUAGES).optional(),
  country: z.string().regex(CASE_COUNTRY_RE).optional(),
  status: z.enum(ADMIN_CASE_STATUSES).optional(),
  kind: z.enum(SAMPLE_CASE_KINDS).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
})
export type AdminCaseListQuery = z.infer<typeof adminCaseListQuerySchema>

/**
 * PATCH /admin/cases/:id. 관리자 칸 3개만 바꾼다 (내용의 원본은 엑셀).
 * adminRank: 작은 수가 위, null 이면 순위 지우기
 */
export const adminCasePatchSchema = z
  .object({
    hidden: z.boolean().optional(),
    startEnabled: z.boolean().optional(),
    adminRank: z.number().int().min(0).max(9999).nullable().optional(),
  })
  .refine(
    (v) => v.hidden !== undefined || v.startEnabled !== undefined || v.adminRank !== undefined,
    { message: 'Nothing to change' },
  )
export type AdminCasePatch = z.infer<typeof adminCasePatchSchema>

/** 사례에서 시작한 제품 수 (simulations.input.sourceCaseId). 실패는 뺀다 */
export interface CaseStartCounts {
  /** 최근 30일 */
  last30d: number
  total: number
}

/** 관리자 표 한 줄 */
export interface AdminCaseView extends CaseCard {
  status: AdminCaseStatus
  isHidden: boolean
  isDeleted: boolean
  startEnabled: boolean
  adminRank: number | null
  sortOrder: number
  /** 같은 번호의 1단계 글 id (사례 표의 값). 템플릿은 글이 없어 '' */
  postId: string
  /** 그 글이 살아 있는지. false 면 '지워짐' 표시 (템플릿은 글이 없어 늘 false) */
  postAlive: boolean
  /** 셰프 크레딧 상태와 관리자 전용 칸 (앱에는 보내지 않는다) */
  chefCreditStatus: ChefCreditStatus
  chefName: ChefName | null
  /** 서면 동의 문서가 보관된 곳 (예: '계약/2026-11 백종원 동의서.pdf') */
  chefConsentRef: string | null
  /** 레시피 출처 메모 (예: '셰프 측 제공 원문') */
  chefRecipeSource: string | null
  chefPhotoKey: string | null
  /** 셰프 구분 id (예: 'gordon-ramsay'). 같은 셰프의 줄을 모아 보는 관리자용 */
  chefId: string | null
  chefPhotoCredit: CasePhotoCredit | null
  chefSource: ChefSourceLink | null
  /** 사진 저작권 표시가 붙은 사진 수 */
  creditedImageCount: number
  /** 시작 수 (queued·running·done) */
  starts: CaseStartCounts
  /** 완료 수 (done) */
  completed: CaseStartCounts
  updatedAt: string
}

/** GET /admin/cases 응답 */
export interface AdminCaseListResponse {
  items: AdminCaseView[]
  total: number
  nextOffset: number | null
}

/** PATCH /admin/cases/:id 응답 */
export interface AdminCasePatchResponse {
  case: AdminCaseView
}
